import { faChevronDown, faChevronUp, faClipboard, faInfoCircle, faPenToSquare, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button, Card, Col, Collapse, Form, ListGroup, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { useContextManagement } from "../hooks/useContextManagement";
import './SectionContextManagement.scss';

interface SectionContextManagementProps {
    className?: string;
    patientVisit: PatientVisit;
    tenant: string;
}

export const SectionContextManagement = ({
    className = '',
    patientVisit,
    tenant,
}: SectionContextManagementProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [newContext, setNewContext] = useState("");

    const {
        context,
        isLoading,
        isSaving,
        loadContext,
        saveContext,
        toggleContext,
        deleteContext,
        addContext,
        cancelEdits,
    } = useContextManagement(patientVisit, tenant);

    // Load context on mount
    useEffect(() => {
        loadContext();
    }, [patientVisit.csn]);

    const handleSaveContext = async () => {
        const updatedContext = JSON.parse(JSON.stringify(context));
        if (newContext) {
            updatedContext.contexts.push({
                text: newContext,
                enabled: true,
            });
        }
        const success = await saveContext(updatedContext);
        if (success) {
            setNewContext("");
            setIsEditing(false);
        }
    };

    const handleCancelEdits = () => {
        cancelEdits();
        setNewContext("");
        setIsEditing(false);
    };

    return (
        <Card className={`fade-in dashboard-section ${className}`}>
            <Card.Body>
                <Row className="align-items-center mb-2">
                    <Col>
                        <div className="d-flex align-items-center">
                            <span className="dashboard-actions-circle-small dashboard-actions-circle-blue me-2">
                                <FontAwesomeIcon icon={faClipboard} />
                            </span>
                            <strong className="ml-2">Additional Context</strong>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip>
                                        Add information to guide Capacity when generating summaries.
                                    </Tooltip>
                                }
                            >
                                <span className="ms-2"><FontAwesomeIcon icon={faInfoCircle} /></span>
                            </OverlayTrigger>
                            {context.contexts.length > 0 && (
                                <small className="text-muted ms-2 ml-2">({context.contexts.filter(c => c.enabled).length} active)</small>
                            )}
                        </div>
                    </Col>
                    <Col className="text-right">
                        <Button
                            className="dashboard-round-btn"
                            variant="outline-secondary"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>

                <Collapse in={isExpanded}>
                    <div>
                        {isLoading ? (
                            <Row className="mt-2">
                                <Col>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Loading context...
                                </Col>
                            </Row>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    {context.contexts.length > 0 ? (
                                        <ListGroup>
                                            {context.contexts.map((ctx: any, idx: number) => (
                                                <ListGroup.Item key={idx}>
                                                    <Row>
                                                        <Col md={1} className="text-right">
                                                            <OverlayTrigger
                                                                placement="bottom"
                                                                overlay={
                                                                    <Tooltip>
                                                                        {ctx.enabled ? <>Include in regeneration</> : <>Exclude in regeneration</>}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <Form.Check
                                                                    type="switch"
                                                                    checked={ctx.enabled}
                                                                    disabled={!isEditing}
                                                                    onChange={() => toggleContext(idx)}
                                                                />
                                                            </OverlayTrigger>
                                                        </Col>
                                                        <Col md={isEditing ? 10 : 11} className="context-text">
                                                            {ctx.text}
                                                        </Col>
                                                        {isEditing && (
                                                            <Col md={1} className="text-right">
                                                                <OverlayTrigger
                                                                    placement="bottom"
                                                                    overlay={
                                                                        <Tooltip>
                                                                            Delete context item
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span
                                                                        onClick={() => deleteContext(idx)}
                                                                        className="context-link-btn"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTimes} />
                                                                    </span>
                                                                </OverlayTrigger>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <div className="text-muted">
                                            <small>No additional context added yet. Click "Edit Context" to add information to help Capacity.</small>
                                        </div>
                                    )}

                                    {isEditing && (
                                        <Form.Control
                                            className="mt-2"
                                            as="textarea"
                                            rows={3}
                                            value={newContext}
                                            placeholder="Enter new context..."
                                            onChange={(event) => setNewContext(event.target.value)}
                                        />
                                    )}

                                    {isEditing ? (
                                        <Row className="mt-2">
                                            <Col className="text-right">
                                                <Button
                                                    variant="outline-secondary"
                                                    style={{marginRight: '8px'}}
                                                    size="sm"
                                                    onClick={handleCancelEdits}
                                                >
                                                    Cancel
                                                </Button>
                                                {!isSaving ? (
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={handleSaveContext}
                                                    >
                                                        Save Changes
                                                    </Button>
                                                ) : (
                                                    <Button variant="secondary" size="sm" disabled>
                                                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                                                    </Button>
                                                )}
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row className="mt-2">
                                            <Col>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => setIsEditing(true)}
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} /> Edit Context
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}
                                </Form.Group>
                            </>
                        )}
                    </div>
                </Collapse>
            </Card.Body>
        </Card>
    );
};
